/**
 * Legal documents a person agrees to. Each has a version so the backend can
 * store exactly which text was accepted and re-ask when it changes.
 * Copy is placeholder prototype text — replace with counsel-approved wording.
 */
export type LegalDocId = 'terms' | 'privacy' | 'businessTerms'

export interface LegalDoc {
  id: LegalDocId
  title: string
  version: string
  effective: string
  intro: string
  sections: { heading: string; body: string }[]
}

export const legalDocs: Record<LegalDocId, LegalDoc> = {
  terms: {
    id: 'terms',
    title: 'Terms of Use',
    version: '2.1',
    effective: '1 September 2026',
    intro:
      'These terms govern your use of Talab, including the Talab app, talab.app and shops and chats powered by Talab.',
    sections: [
      {
        heading: 'Your account',
        body: 'You need to be at least 18 to create an account. Keep your sign-in details private — you are responsible for activity on your account.',
      },
      {
        heading: 'Using Talab',
        body: 'Talab helps you find, ask about and buy from businesses. Businesses are responsible for their own products, prices, stock and fulfilment. Talab is not the seller unless it says so.',
      },
      {
        heading: 'AI answers',
        body: 'Answers are generated from information businesses provide. Always confirm important details — price, availability, terms — before you commit.',
      },
      {
        heading: 'Acceptable use',
        body: 'Do not misuse Talab, impersonate others, post unlawful content or try to disrupt the service. We may suspend accounts that do.',
      },
      {
        heading: 'Changes',
        body: 'If we change these terms in a meaningful way, we will ask you to review and accept them again.',
      },
    ],
  },
  privacy: {
    id: 'privacy',
    title: 'Privacy Policy',
    version: '1.4',
    effective: '1 September 2026',
    intro:
      'This explains what information Talab collects, why, and the choices you have.',
    sections: [
      {
        heading: 'What we collect',
        body: 'Your name, phone number or email, the conversations you have with Talab and with businesses, and orders you place.',
      },
      {
        heading: 'How we use it',
        body: 'To run your account, answer your questions, connect you with businesses you choose, keep Talab secure and improve the service.',
      },
      {
        heading: 'What businesses see',
        body: 'A business only sees your activity with that business — never your activity with other businesses on Talab or your wider browsing.',
      },
      {
        heading: 'Your choices',
        body: 'You can view, download or delete your data from your profile at any time, and withdraw marketing consent whenever you like.',
      },
      {
        heading: 'Retention',
        body: 'We keep your information while your account is active and for as long as the law requires afterwards.',
      },
    ],
  },
  businessTerms: {
    id: 'businessTerms',
    title: 'Business Terms & Conditions',
    version: '1.2',
    effective: '1 September 2026',
    intro:
      'These terms apply when you use the Talab Business Hub to sell, rent or offer anything through Talab — whether you are an individual seller or a registered company. They are in addition to the Terms of Use.',
    sections: [
      {
        heading: 'Who is agreeing',
        body: 'If you sell as an individual, you agree to these terms personally. If you sell for a company, you confirm you are authorised to accept them on its behalf.',
      },
      {
        heading: 'Your information',
        body: 'The details, licences (where required) and catalogue you provide must be accurate and kept up to date.',
      },
      {
        heading: 'What you sell',
        body: 'You are the seller of record. You are responsible for your prices, stock, fulfilment, refunds and compliance with the laws that apply to your trade.',
      },
      {
        heading: 'Your AI assistant',
        body: 'Talab answers customers using the catalogue, rules and content you provide. Review it regularly — you are responsible for what it is instructed to say.',
      },
      {
        heading: 'Customer data',
        body: 'You may only use customer information to serve that customer, in line with the Privacy Policy and applicable data protection law. You may not export it for unrelated marketing.',
      },
      {
        heading: 'Plans and billing',
        body: 'Paid plans renew automatically until cancelled. You can change or cancel your plan from Settings at any time.',
      },
    ],
  },
}
